// SERVER-SIDE RENDER route (internal). The robot browser (Cloudflare Browser
// Rendering, driven by the worker's POST /pdf/server-generate/:id) navigates
// here for a given order. It runs the EXACT client render+upload pipeline —
// renderAndUploadAlbumPdfs from multiAlbumPdf (same code the customer's browser
// runs) — for every album, which uploads the cover/interior PDFs to the order's
// storage paths + calls pdfFinalize (sets pdf_status='ready', urls, source hash)
// + regenerates the flipbook preview. Idempotent: force=0 skips albums whose
// source hash is unchanged. Reports outcome on window.__storizRender so the
// worker can read it.
//
// Gated by a server-only probe token. Not linked from any UI.
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { coversApi, settingsApi, ordersApi, sessionsApi } from '@/lib/api';
import { renderAndUploadAlbumPdfs, lastAlbumRenderError } from '@/lib/multiAlbumPdf';
import type { AlbumCouponOutcome, CouponFinish, OrderCouponPlan } from '@/lib/couponPrint';
import { applyRenderObjectCache } from '@/lib/renderObjectCacheSwitch';

const PROBE = 'sz-bp-9f2c7a1e8b3d4e';

export default function ServerRenderProbe() {
  const [sp] = useSearchParams();
  const ran = useRef(false);
  const [msg, setMsg] = useState('starting…');

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const W = window as any;
    W.__storizRender = { done: false };
    const order = sp.get('order');
    const force = sp.get('force') === '1';
    const albums = (sp.get('albums') ?? '1')
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => Number.isFinite(n) && n > 0);

    (async () => {
      const t0 = Date.now();
      try {
        if (sp.get('probe') !== PROBE || !order || albums.length === 0) {
          W.__storizRender = { done: true, ok: false, error: 'bad probe / order / albums' };
          setMsg('forbidden');
          return;
        }
        setMsg('loading covers + settings…');
        const covers = (await coversApi.list()).covers;
        const settings = (await settingsApi.get()).settings;
        // Download originals through the worker's R2 copy when ticked (migration 269).
        applyRenderObjectCache(settings);
        // Fonts must be loaded before text rasterizes (render-parity contract).
        try { await (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts?.ready; } catch { /* ignore */ }

        // ── COUPONS (plan 2026-10-28) — THE print render is the only caller ──
        // print-data issues the order's codes, so it is asked here and nowhere
        // else (never by a preview). Switch off → `enabled:false` → no plan →
        // every album below renders exactly as before. A coupon failure is
        // logged and never fails the render.
        let couponPlan: OrderCouponPlan | null = null;
        const couponOutcomes = new Map<number, AlbumCouponOutcome>();
        if ((settings as { coupons_enabled?: boolean } | null)?.coupons_enabled !== false) {
          try {
            setMsg('coupons…');
            const { planOrderCoupons, fetchCouponPrintData } = await import('@/lib/couponPrint');
            const orderRow = await ordersApi.get(order).then((r) => r.order).catch(() => null);
            const bundleQty = ({ Solo: 1, Duo: 2, Trio: 3 } as Record<string, number>)[orderRow?.bundle ?? 'Solo'] ?? 1;
            couponPlan = await planOrderCoupons({
              fetchData: () => fetchCouponPrintData(order),
              getSession: (i) => sessionsApi.get(order, i).then((r) => r.session ?? null),
              // EVERY album of the order decides, not only the ones this run rebuilds.
              albumIndexes: [
                ...albums,
                ...Array.from({ length: bundleQty }, (_, i) => i + 1),
                ...((orderRow?.album_pdfs ?? []).map((r) => r.album_index)),
              ],
              introUrl: settings?.intro_page_1_url ?? null,
              outroUrl: settings?.outro_page_2_url ?? null,
            });
            if (couponPlan) console.info('[coupons] plan', JSON.stringify(couponPlan.pick ?? 'separate pdf'));
          } catch (e) {
            console.warn('[coupons] plan failed — no coupon in this render:', e instanceof Error ? e.message : String(e));
            couponPlan = null;
          }
        }

        const results: { album: number; ok: boolean }[] = [];
        for (const albumIndex of albums) {
          setMsg(`rendering + uploading album ${albumIndex}…`);
          const albumCoupons = couponPlan?.couponsFor(albumIndex) ?? null;
          let couponPages: number[] | null = null;
          const ok = await renderAndUploadAlbumPdfs({
            shopifyOrderId: order,
            albumIndex,
            covers,
            logoUrl: settings?.logo_url ?? null,
            introPageUrl: settings?.intro_page_1_url ?? null,
            outroPageUrl: settings?.outro_page_2_url ?? null,
            bleedMm: settings?.pdf_bleed_mm ?? 3,
            force,
            // The editor's « Repères de montage » (admin regenerate on the server, 2026-10-15).
            showAssemblyGuides: sp.get('guides') === '1',
            ...(albumCoupons ? {
              coupons: albumCoupons,
              couponHash: couponPlan?.hashFor(albumIndex) ?? null,
              onCouponPages: (pages: number[] | null) => { couponPages = pages; },
            } : {}),
          });
          results.push({ album: albumIndex, ok });
          couponOutcomes.set(albumIndex, { ok, pages: couponPages });
        }

        let coupons: CouponFinish | null = null;
        if (couponPlan) {
          setMsg('coupons — finishing…');
          const { finishOrderCoupons } = await import('@/lib/couponPrint');
          coupons = await finishOrderCoupons({
            plan: couponPlan,
            shopifyOrderId: order,
            outcomes: couponOutcomes,
            bleedMm: settings?.pdf_bleed_mm ?? 3,
          });
          console.info('[coupons] result', JSON.stringify(coupons));
        } else if (settings && 'coupons_enabled' in settings) {
          // No coupon in this render (switch off, no campaign, plan failed):
          // clear a stale « pages 39–40 » note so the printer page never asks
          // to cut coupons out of a book that no longer has them. Only once the
          // coupon migration exists (the column is then present in settings).
          const { postCouponPrintResult } = await import('@/lib/couponPrint');
          await postCouponPrintResult(order, { clear: true, rendered_albums: albums }).catch(() => {});
        }

        W.__storizRender = {
          done: true,
          ok: results.every((r) => r.ok),
          ...(results.every((r) => r.ok) ? {} : { error: lastAlbumRenderError ?? `album(s) ${results.filter((r) => !r.ok).map((r) => r.album).join(', ')} failed` }),
          results,
          ...(coupons ? { coupons } : {}),
          ms: Date.now() - t0,
        };
        setMsg('done ✅');
      } catch (e) {
        W.__storizRender = { done: true, ok: false, error: e instanceof Error ? (e.stack ?? e.message) : String(e), ms: Date.now() - t0 };
        setMsg('error');
      }
    })();
  }, [sp]);

  return <div style={{ padding: 20, fontFamily: 'sans-serif' }}>Server render — {msg}</div>;
}
